import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clearAuth, useAuth } from '@/features/auth/authStore';
import { decodeJwt } from '@/features/auth/jwt';
import { KNOWN_REPORTS } from '@/features/dashboard/config';
import { AdminApiError, listUsers, updateUser, type AdminUser } from '@/api/admin';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { UserFormModal } from '@/features/admin/UserFormModal';
import styles from '@/features/admin/AdminUsersPage.module.css';

const USERS_KEY = ['admin', 'users'] as const;

/** Sentinel for "don't filter by client" — no real client id can collide with it. */
const CLIENT_ALL = '__all__';

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function reportLabel(id: string): string {
  return KNOWN_REPORTS.find((r) => r.id === id)?.label ?? id;
}

export function AdminUsersPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUserId = auth?.token ? (decodeJwt(auth.token)?.sub ?? null) : null;

  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState<string>(CLIENT_ALL);
  const [editing, setEditing] = useState<AdminUser | 'new' | null>(null);

  const usersQuery = useQuery({ queryKey: USERS_KEY, queryFn: listUsers });

  const toggleActive = useMutation({
    mutationFn: (u: AdminUser) => updateUser(u.id, { active: !u.active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });

  /*
   * The client options come from the users actually loaded, never from a
   * hardcoded list: a new client appears the moment its first user exists, and
   * one whose last user moved away stops being offered. Counts are shown so an
   * admin can see the split without applying the filter.
   */
  const clientOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const u of usersQuery.data ?? []) counts.set(u.client_id, (counts.get(u.client_id) ?? 0) + 1);
    return [...counts].map(([id, count]) => ({ id, count })).sort((a, b) => a.id.localeCompare(b.id));
  }, [usersQuery.data]);

  // A selection that no longer exists (its last user was moved, or the list was
  // refetched) must not leave the table mysteriously empty.
  const activeClient = clientOptions.some((c) => c.id === clientFilter) ? clientFilter : CLIENT_ALL;

  const filtered = useMemo(() => {
    const list = usersQuery.data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter(
      (u) =>
        (activeClient === CLIENT_ALL || u.client_id === activeClient) &&
        (q === '' || u.email.toLowerCase().includes(q) || u.client_id.toLowerCase().includes(q)),
    );
  }, [usersQuery.data, search, activeClient]);

  function onSignOut() {
    clearAuth();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className={styles.page}>
      <div className={styles.bar}>
        <div>
          <div className={styles.barTitle}>User management</div>
          <div className={styles.barSub}>IceMobile admin · {auth?.clientId ?? ''}</div>
        </div>
        <div className={styles.barActions}>
          <Button variant="link" onClick={() => navigate('/admin/runs')}>
            Report runs
          </Button>
          <Button variant="link" onClick={() => navigate('/admin/config')}>
            Pipeline config
          </Button>
          <Button variant="link" onClick={() => navigate('/')}>
            ← Back to reports
          </Button>
          <Button variant="link" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            <input className={styles.search} placeholder="Search by email or client…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select
              className={styles.clientFilter}
              aria-label="Filter by client"
              value={activeClient}
              onChange={(e) => setClientFilter(e.target.value)}
              disabled={clientOptions.length === 0}
            >
              <option value={CLIENT_ALL}>All clients ({usersQuery.data?.length ?? 0})</option>
              {clientOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.id} ({c.count})
                </option>
              ))}
            </Select>
          </div>
          <Button variant="primary" onClick={() => setEditing('new')}>
            + New user
          </Button>
        </div>

        {toggleActive.isError && <Alert variant="error">{(toggleActive.error as AdminApiError)?.message ?? 'Update failed'}</Alert>}

        <div className={styles.tableWrap}>
          {usersQuery.isLoading ? (
            <div className={styles.state}>Loading users…</div>
          ) : usersQuery.isError ? (
            <div className={styles.state}>Could not load users: {(usersQuery.error as AdminApiError)?.message ?? 'unknown error'}</div>
          ) : filtered.length === 0 ? (
            <div className={styles.state}>No users{search || activeClient !== CLIENT_ALL ? ' match the current filter' : ''}.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Client</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Reports</th>
                  <th>Last login</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isSelf = u.id === currentUserId;
                  return (
                    <tr key={u.id} className={u.active ? undefined : styles.rowInactive}>
                      <td className={styles.email}>{u.email}</td>
                      <td className={styles.muted}>{u.client_id}</td>
                      <td>
                        <Badge variant={u.role === 'admin' ? 'admin' : 'client'}>{u.role}</Badge>
                      </td>
                      <td>
                        <Badge variant={u.active ? 'active' : 'inactive'}>{u.active ? 'active' : 'inactive'}</Badge>
                      </td>
                      <td>
                        {u.role === 'admin' ? (
                          <span className={styles.muted}>all reports</span>
                        ) : u.allowed_reports.length === 0 ? (
                          <span className={styles.muted}>none</span>
                        ) : (
                          <div className={styles.chips}>
                            {u.allowed_reports.map((r) => (
                              <span key={r} className={styles.chip} title={r}>
                                {reportLabel(r)}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className={styles.muted}>{fmtDate(u.last_login_at)}</td>
                      <td>
                        <div className={styles.actions}>
                          <Button variant="ghost" onClick={() => setEditing(u)}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            disabled={isSelf || toggleActive.isPending}
                            title={isSelf ? "You can't deactivate your own account" : undefined}
                            onClick={() => toggleActive.mutate(u)}
                          >
                            {u.active ? 'Deactivate' : 'Activate'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && (
        <UserFormModal
          mode={editing === 'new' ? 'create' : 'edit'}
          user={editing === 'new' ? undefined : editing}
          currentUserId={currentUserId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: USERS_KEY });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
